import React, { useState } from 'react';
import { requestPasswordChange } from '../api';
import detailMessage from '../detailMessage';
import { useLang } from '../LangContext';

interface Props {
  token: string;
}

interface PasswordForm {
  current: string;
  next: string;
  confirm: string;
}

const PasswordChangeForm = ({ token }: Props) => {
  const { t } = useLang();
  const [form, setForm] = useState<PasswordForm>({ current: '', next: '', confirm: '' });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (form.next.length < 8) {
      setError(t.newPasswordTooShort);
      return;
    }
    if (form.next !== form.confirm) {
      setError(t.passwordsDoNotMatch);
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestPasswordChange(form.current, form.next, token);
      setSuccess(res.message || t.passwordChangeConfirmationSent);
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setError(detailMessage(err, t.passwordChangeFailed));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="password-change-section">
      <h4>{t.changePasswordHeading}</h4>
      <p className="duration-options-hint">
        {t.changePasswordHint}
      </p>

      {error && <div className="error-message"><strong>{t.errorLabel2}</strong> {error}</div>}
      {success && <div className="success-message"><strong>✓</strong> {success}</div>}

      <form className="profile-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="pw-current">{t.currentPasswordLabel}</label>
          <input
            id="pw-current"
            type="password"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            disabled={submitting}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="pw-new">{t.newPasswordLabel}</label>
          <input
            id="pw-new"
            type="password"
            value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
            disabled={submitting}
            minLength={8}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="pw-confirm">{t.confirmNewPasswordLabel}</label>
          <input
            id="pw-confirm"
            type="password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            disabled={submitting}
            minLength={8}
            required
          />
        </div>
        <button type="submit" className="submit-button" disabled={submitting}>
          {submitting ? t.sendingButton : t.changePasswordButton}
        </button>
      </form>
    </section>
  );
};

export default PasswordChangeForm;
